import { useState, useCallback } from 'react';

/**
 * Persists state to localStorage with a JSON-serialised value.
 *
 * @param {string} key
 * @param {*} initialValue
 * @returns {[*, Function, Function]} [value, setValue, removeValue]
 */
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item !== null ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const next = value instanceof Function ? value(storedValue) : value;
        setStoredValue(next);
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        console.warn(`[useLocalStorage] Failed to set "${key}"`);
      }
    },
    [key, storedValue]
  );

  const removeValue = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch {
      console.warn(`[useLocalStorage] Failed to remove "${key}"`);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue];
};

export default useLocalStorage;
